import { DOCUMENT_CONFIG } from "./documentDetectionConfig";
import { normalizeDetectorCorners } from "./DocumentCoordinates";
import {
  assessDocumentCompliance,
  createEmptyCompliance,
  getComplianceStatus,
} from "./DocumentGeometry";

const getCornerMovement = (previousCorners, nextCorners) => {
  if (previousCorners?.length !== 4 || nextCorners?.length !== 4) return 0;

  return (
    nextCorners.reduce(
      (total, corner, index) =>
        total +
        Math.hypot(
          corner.x - previousCorners[index].x,
          corner.y - previousCorners[index].y,
        ),
      0,
    ) / 4
  );
};

// 对四个角分别做指数移动平均，减少检测器逐帧输出造成的边框跳动。
// 公式：平滑值 = 上一帧 + (当前帧 - 上一帧) * emaAlpha。
const smoothCorners = (previousCorners, nextCorners) => {
  if (nextCorners?.length !== 4) return [];
  if (previousCorners?.length !== 4) return nextCorners;

  return nextCorners.map((corner, index) => ({
    x:
      previousCorners[index].x +
      (corner.x - previousCorners[index].x) * DOCUMENT_CONFIG.emaAlpha,
    y:
      previousCorners[index].y +
      (corner.y - previousCorners[index].y) * DOCUMENT_CONFIG.emaAlpha,
  }));
};

/**
 * 有状态的跨帧追踪器。
 * 单帧几何判断由 DocumentGeometry 完成；本类负责平滑、短暂漏检保持和稳定计时。
 */
export class DocumentTracker {
  constructor() {
    this.reset();
  }

  reset() {
    this.corners = [];
    this.lastCompliance = createEmptyCompliance();
    this.missedFrames = 0;
    this.stableFrames = 0;
    this.stableElapsedMs = 0;
    this.lastTimestamp = 0;
    this.fpsStartedAt = 0;
    this.fpsFrameCount = 0;
    this.detectorFps = 0;
  }

  updateFps(timestamp) {
    if (!this.fpsStartedAt) this.fpsStartedAt = timestamp;
    this.fpsFrameCount += 1;
    const elapsed = timestamp - this.fpsStartedAt;

    if (elapsed >= 500) {
      this.detectorFps = (this.fpsFrameCount * 1000) / elapsed;
      this.fpsStartedAt = timestamp;
      this.fpsFrameCount = 0;
    }
  }

  createResult(compliance, details = {}) {
    // stable 是最终拍照开关：严格合格必须连续维持 stableDuration。
    const stable =
      compliance.valid &&
      this.stableElapsedMs >= DOCUMENT_CONFIG.stableDuration;

    return {
      ...compliance,
      ...details,
      stable,
      stableFrames: this.stableFrames,
      stableElapsedMs: this.stableElapsedMs,
      stableProgress: Math.min(
        this.stableElapsedMs / DOCUMENT_CONFIG.stableDuration,
        1,
      ),
      missedFrames: this.missedFrames,
      detectorFps: this.detectorFps,
      status: getComplianceStatus(compliance, stable),
    };
  }

  update({ detectorOutput, frameWidth, frameHeight, timestamp }) {
    this.updateFps(timestamp);
    const frameAspectRatio = frameWidth / Math.max(frameHeight, 1);
    const frameDuration = this.lastTimestamp
      ? Math.max(0, timestamp - this.lastTimestamp)
      : 0;
    this.lastTimestamp = timestamp;
    const hasFreshCorners =
      detectorOutput?.detected && detectorOutput.corners?.length === 4;

    if (!hasFreshCorners) {
      this.missedFrames += 1;

      // 偶发漏检时暂时保留上一帧四角，避免追踪框闪烁；
      // 连续漏检超过 maxMissedFrames 后才清空，界面恢复为中央默认四角。
      if (
        this.corners.length === 4 &&
        this.missedFrames <= DOCUMENT_CONFIG.maxMissedFrames
      ) {
        return this.createResult(this.lastCompliance, {
          fresh: false,
          trackingHeld: true,
          detectorOutput,
        });
      }

      this.corners = [];
      this.stableFrames = 0;
      this.stableElapsedMs = 0;
      this.lastCompliance = createEmptyCompliance(frameAspectRatio);
      return this.createResult(this.lastCompliance, {
        fresh: false,
        trackingHeld: false,
        detectorOutput,
      });
    }

    this.missedFrames = 0;
    // 扫描器输出的是采样画布像素坐标；先转成 0~1 坐标，
    // 后续判断就不会受到手机分辨率或横竖屏尺寸的影响。
    const normalizedCorners = normalizeDetectorCorners(
      detectorOutput.corners,
      frameWidth,
      frameHeight,
    );
    const movement = getCornerMovement(this.corners, normalizedCorners);
    this.corners = smoothCorners(this.corners, normalizedCorners);
    const compliance = assessDocumentCompliance({
      detected: true,
      corners: this.corners,
      confidence: detectorOutput.confidence,
      frameAspectRatio,
    });

    // 稳定计时采用严格合格条件，而不是预录制容错条件。
    // 因此录像可以提前开始，但按钮/自动拍照不会因为容错而提前放行。
    const frameIsStable =
      compliance.valid && movement <= DOCUMENT_CONFIG.maxStableMovement;

    if (frameIsStable) {
      this.stableFrames += 1;
      this.stableElapsedMs += Math.min(frameDuration, 250);
    } else {
      this.stableFrames = 0;
      this.stableElapsedMs = 0;
    }

    this.lastCompliance = compliance;
    return this.createResult(compliance, {
      fresh: true,
      trackingHeld: false,
      movement,
      detectorOutput,
    });
  }
}
